// src/pages/RecipePage.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Nav            from "../components/Nav";
import Toast          from "../components/Toast";
import { useAuth }    from "../App";
import { fetchRecipe, deleteRecipe } from "../db";
import { getCat, formatDate, copyLink } from "../constants";

export default function RecipePage() {
  const { id }  = useParams();
  const nav     = useNavigate();
  const { allowed } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast,  setToast]  = useState(null);

  useEffect(() => {
    fetchRecipe(id).then(r => { setRecipe(r); setLoading(false); });
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("למחוק את המתכון לצמיתות?")) return;
    await deleteRecipe(id, recipe?.imageUrl);
    nav("/");
  };

  const handleShare = () => {
    copyLink(id);
    setToast("הקישור הועתק ✓");
  };

  if (loading) return (
    <div style={{ background:"var(--cream)", minHeight:"100vh" }}>
      <Nav />
      <p style={{ textAlign:"center", padding:80, fontSize:11, letterSpacing:4, color:"var(--warm)" }}>LOADING…</p>
    </div>
  );

  if (!recipe) return (
    <div style={{ background:"var(--cream)", minHeight:"100vh" }}>
      <Nav />
      <p style={{ textAlign:"center", padding:80, color:"var(--warm)" }}>
        המתכון לא נמצא. <button onClick={() => nav("/")} style={{ color:"var(--burg)", textDecoration:"underline" }}>חזרה</button>
      </p>
    </div>
  );

  const cat  = getCat(recipe.category);
  const tags = (recipe.tags || "").split(",").map(t => t.trim()).filter(Boolean);

  // Backward-compatible image list
  const images = recipe.images?.length
    ? recipe.images
    : recipe.imageUrl ? [recipe.imageUrl] : [];

  const youtubeId = getYouTubeId(recipe.youtubeUrl);

  return (
    <div style={{ background:"var(--cream)", minHeight:"100vh" }} className="fade-up">
      <Nav />

      <main style={{ maxWidth:860, margin:"0 auto", padding:"0 32px 80px" }}>
        <button style={s.back} onClick={() => nav(-1)}>← חזרה לארכיון</button>

        {/* ── Image Gallery ──────────────────────────── */}
        {images.length > 0 && <Gallery images={images} title={recipe.title} />}
        {/* ─────────────────────────────────────────── */}

        <div style={s.header} className="article-padding">
          <div style={s.meta}>
            <span style={s.metaCat}>{cat.label}</span>
            <span style={s.dot}>·</span>
            <span style={s.metaDate}>{formatDate(recipe.createdAt)}</span>
            {recipe.author && <><span style={s.dot}>·</span><span style={s.metaDate}>{recipe.author}</span></>}
          </div>

          <h1 style={s.title} className="article-title">{recipe.title}</h1>

          {recipe.description && (
            <p style={s.lead} className="article-lead">{recipe.description}</p>
          )}

          <div style={s.statRow} className="stat-row">
            {recipe.difficulty && <Stat label="רמת קושי"  val={recipe.difficulty} />}
            {recipe.prepTime   && <Stat label="הכנה"       val={`${recipe.prepTime} דקות`} />}
            {recipe.cookTime   && <Stat label="בישול"      val={`${recipe.cookTime} דקות`} />}
            {recipe.servings   && <Stat label="מנות"       val={recipe.servings} />}
          </div>

          {recipe.wine && (
            <div style={s.wineBar} className="wine-bar">
              <span style={s.wineLabel}>🍷 Wine Pairing</span>
              <span style={s.wineVal}>{recipe.wine}</span>
            </div>
          )}
        </div>

        <div style={s.rule} />

        <div style={s.body} className="article-body">
          <aside style={s.ingredientsCol}>
            <h3 style={s.colHead}>מרכיבים</h3>
            {(recipe.ingredients || "").split("\n").filter(Boolean).map((line, i) => (
              <div key={i} style={s.ingLine}>
                <span style={s.dash}>—</span>
                <span style={s.ingText}>{line}</span>
              </div>
            ))}
          </aside>

          <div style={s.instructionsCol}>
            <h3 style={s.colHead}>הכנה</h3>
            {(recipe.instructions || "").split("\n\n").filter(Boolean).map((para, i) => (
              <p key={i} style={s.para}>{para.trim()}</p>
            ))}
          </div>
        </div>

        {recipe.notes && (
          <div style={s.notes}>
            <div style={s.notesLabel}>הערות השף</div>
            <p style={s.notesText}>{recipe.notes}</p>
          </div>
        )}

        {/* ── YouTube Embed ──────────────────────────── */}
        {youtubeId && (
          <div style={s.youtubeWrap}>
            <div style={s.youtubeLabel}>
              <span style={{ marginLeft:6 }}>▶</span> סרטון הכנה
            </div>
            <div style={s.youtubeFrame}>
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }}
              />
            </div>
          </div>
        )}
        {/* ─────────────────────────────────────────── */}

        {tags.length > 0 && (
          <div style={s.tagRow}>
            {tags.map(t => <span key={t} style={s.tag}>#{t}</span>)}
          </div>
        )}

        <div style={s.rule} />

        <div style={s.actions} className="action-bar">
          <Btn onClick={handleShare} variant="outline">⬡ שתף קישור</Btn>
          {allowed && (
            <>
              <Btn onClick={() => nav(`/edit/${id}`)} variant="outline">✎ עריכה</Btn>
              <Btn onClick={handleDelete} variant="danger">✕ מחיקה</Btn>
            </>
          )}
        </div>
      </main>

      {toast && <Toast msg={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

/* ── Gallery component ───────────────────────────────── */
function Gallery({ images, title }) {
  const [mainIdx, setMainIdx] = useState(0);

  return (
    <div style={{ marginBottom:40 }}>
      {/* Main image */}
      <div style={gs.hero} className="detail-hero">
        <img
          src={images[mainIdx]}
          alt={title}
          style={gs.heroImg}
          key={mainIdx}  // force re-render for fade effect
          className="gallery-main-img"
        />
      </div>

      {/* Thumbnails — shown only when more than 1 image */}
      {images.length > 1 && (
        <div style={gs.thumbStrip}>
          {images.map((url, i) => (
            <div
              key={i}
              onClick={() => setMainIdx(i)}
              style={{
                ...gs.thumbItem,
                outline: i === mainIdx ? "2px solid var(--ink)" : "2px solid transparent",
                outlineOffset: 2,
                opacity: i === mainIdx ? 1 : 0.55,
              }}
            >
              <img src={url} alt="" style={gs.thumbImg} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const gs = {
  hero:      { height:440, overflow:"hidden", position:"relative", background:"var(--tint)" },
  heroImg:   { width:"100%", height:"100%", objectFit:"cover", transition:"opacity .25s" },
  thumbStrip:{ display:"flex", gap:8, marginTop:8, overflowX:"auto", paddingBottom:2 },
  thumbItem: { width:90, height:66, flexShrink:0, cursor:"pointer", overflow:"hidden", transition:"opacity .2s, outline .2s" },
  thumbImg:  { width:"100%", height:"100%", objectFit:"cover" },
};

/* ── YouTube helper ──────────────────────────────────── */
function getYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&\s#]+)/,
    /youtu\.be\/([^?\s#]+)/,
    /youtube\.com\/embed\/([^?\s#]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

/* ── Sub-components ──────────────────────────────────── */
const Stat = ({ label, val }) => (
  <div style={{ flex:1, padding:"14px 0", textAlign:"center", borderRight:"1px solid var(--rule)" }}>
    <div style={{ fontSize:8, letterSpacing:3, textTransform:"uppercase", color:"var(--warm)", marginBottom:6 }}>{label}</div>
    <div style={{ fontFamily:"var(--serif)", fontSize:15 }}>{val}</div>
  </div>
);

const Btn = ({ onClick, variant, children }) => {
  const v = variant === "danger"
    ? { border:"1px solid var(--burg)", color:"var(--burg)" }
    : { border:"1px solid var(--ink)", color:"var(--ink)" };
  return (
    <button onClick={onClick} className={variant === "danger" ? "btn-burg" : "btn-ink"}
      style={{ ...v, padding:"10px 24px", fontSize:11, letterSpacing:2, textTransform:"uppercase", fontWeight:500, transition:"all .2s", background:"transparent" }}>
      {children}
    </button>
  );
};

/* ── Styles ──────────────────────────────────────────── */
const s = {
  back:          { fontSize:11, letterSpacing:2, color:"var(--warm)", textTransform:"uppercase", marginTop:24, marginBottom:28, display:"inline-block", transition:"color .2s" },
  header:        { marginBottom:28 },
  meta:          { display:"flex", gap:10, alignItems:"center", marginBottom:14 },
  metaCat:       { fontSize:9, letterSpacing:3, textTransform:"uppercase", color:"var(--burg)" },
  dot:           { color:"var(--rule)" },
  metaDate:      { fontSize:9, letterSpacing:2, color:"var(--warm)", textTransform:"uppercase" },
  title:         { fontFamily:"var(--serif)", fontSize:48, fontWeight:400, lineHeight:1.15, marginBottom:20 },
  lead:          { fontSize:16, color:"var(--warm)", lineHeight:1.7, fontStyle:"italic", marginBottom:28, fontFamily:"var(--serif)" },
  statRow:       { display:"flex", borderTop:"1px solid var(--rule)", borderBottom:"1px solid var(--rule)", margin:"24px 0" },
  wineBar:       { background:"var(--tint)", padding:"14px 20px", display:"flex", alignItems:"center", gap:14 },
  wineLabel:     { fontSize:10, letterSpacing:2, color:"var(--burg)", textTransform:"uppercase", flexShrink:0 },
  wineVal:       { fontSize:13 },
  rule:          { height:1, background:"var(--rule)", margin:"32px 0" },
  body:          { display:"grid", gridTemplateColumns:"1fr 2fr", gap:48, marginBottom:40 },
  ingredientsCol:{ borderTop:"2px solid var(--ink)", paddingTop:20 },
  instructionsCol:{ borderTop:"2px solid var(--ink)", paddingTop:20 },
  colHead:       { fontFamily:"var(--serif)", fontSize:12, letterSpacing:3, textTransform:"uppercase", fontWeight:400, marginBottom:24, color:"var(--warm)" },
  ingLine:       { display:"flex", gap:10, marginBottom:12, fontSize:14, lineHeight:1.5 },
  dash:          { color:"var(--burg)", fontFamily:"var(--serif)", flexShrink:0 },
  ingText:       {},
  para:          { fontSize:15, lineHeight:1.85, marginBottom:22, color:"#2a2a2a" },
  notes:         { background:"var(--tint)", borderRight:"3px solid var(--burg)", padding:"20px 24px", marginBottom:24 },
  notesLabel:    { fontSize:9, letterSpacing:3, textTransform:"uppercase", color:"var(--burg)", marginBottom:10 },
  notesText:     { fontSize:14, lineHeight:1.7, fontStyle:"italic" },
  // YouTube
  youtubeWrap:   { marginBottom:32 },
  youtubeLabel:  { fontSize:9, letterSpacing:3, textTransform:"uppercase", color:"var(--warm)", marginBottom:12 },
  youtubeFrame:  { position:"relative", paddingBottom:"56.25%", height:0, overflow:"hidden", background:"#000" },
  // Tags & actions
  tagRow:        { display:"flex", gap:8, flexWrap:"wrap", marginBottom:32 },
  tag:           { fontSize:11, letterSpacing:1, padding:"4px 10px", border:"1px solid var(--rule)", color:"var(--warm)" },
  actions:       { display:"flex", gap:12, flexWrap:"wrap", paddingBottom:40 },
};
